import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import {
  MeetingActionOperationView,
  MeetingActionProposedAction,
  ProposedActionStatus,
} from '../chat/types';

type MeetingActionProposalResponse = {
  id: string;
  eventId: string | null;
  summary: string;
  status: ProposedActionStatus;
  failureReason: string | null;
  operations: MeetingActionOperationView[];
};

export function toMeetingActionProposedAction(
  proposal: MeetingActionProposalResponse,
): MeetingActionProposedAction {
  return {
    kind: 'MEETING_ACTIONS',
    proposalId: proposal.id,
    summary: proposal.summary,
    operations: proposal.operations,
    status: proposal.status,
    failureReason: proposal.failureReason,
  };
}

export function usePendingMeetingActionProposals(
  conversationId: string | undefined,
  enabled: boolean,
) {
  const { data, isError } = useQuery<MeetingActionProposalResponse[]>({
    queryKey: ['conversations', conversationId, 'meeting-action-proposals', { status: 'PENDING' }],
    enabled: enabled && !!conversationId,
    // Flag-gating means a 404 during rollout; never retry or surface it as an error state.
    retry: false,
  });
  return { pendingMeetingActionProposals: isError ? [] : (data ?? []) };
}

export function useMeetingActionProposals(conversationId: string) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    { data: MeetingActionProposalResponse },
    Error,
    { proposalId: string; decision: 'APPROVE' | 'DENY' }
  >({
    mutationFn: ({ proposalId, decision }) =>
      api.post<MeetingActionProposalResponse>(
        `conversations/${conversationId}/meeting-action-proposals/${proposalId}/decision`,
        { decision },
      ),
    onSuccess: response => {
      if (response.data.status === 'APPLIED' && response.data.eventId) {
        // The approved batch changed the meeting's actions, so the Actions tab must re-fetch
        // rather than keep showing what it had before the chat changed it.
        queryClient.invalidateQueries({
          queryKey: ['calendar-events', response.data.eventId, 'actions'],
        });
      }
    },
    // Invalidate on failure too: a 409/404 means the proposal was decided elsewhere, and the
    // pending list must refresh so an orphan widget disappears rather than staying clickable.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'meeting-action-proposals'],
      });
    },
  });

  return { decideMeetingActionProposal: mutateAsync, isDecidingMeetingAction: isPending };
}
